import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { UserCapabilities, HeaderLink, Config } from '@other/interfaces';
import { catchError, tap, map } from 'rxjs/operators';
import { BehaviorSubject, firstValueFrom, Observable, of } from 'rxjs';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { ConfigState } from '@store/config.state';
import { LogService } from "@services/log.service";
@Injectable()
export class CognosService {

  /** Just-In-Time Config selector */
  @SelectSnapshot(ConfigState) config: Config;
  
  constructor(
    private http: HttpClient,
    private logger: LogService
  ) {
    (window as any).cognos = this;
  }

  // Object containing user capabilities, null in development
  userCapabilities = new BehaviorSubject<UserCapabilities>(null);

  /**
   * Gets the available reports based on the user capabilities object
  */
  getLinksWithAccess() {
    // Retrieve links from config and filter by enabled object
    const links: HeaderLink[] = this.config.reportLinks.filter(link => this.config.enableReports[link.text]);
    
    // Check for permissions to every report in Production
    this.logger.info("getLinksWithAccess","CognosService", "links: "+JSON.stringify(links))
    this.logger.info("getLinksWithAccess","CognosService", "this.config.corpintra: "+this.config.corpintra)
    
    if (this.config.corpintra) {
      // Retrieve user capabilities for the current target
      this.logger.info("getLinksWithAccess","CognosService", "userCapabilities: "+JSON.stringify(this.userCapabilities.getValue()))
      if (this.userCapabilities.getValue() === null) {
        this.logger.info("getLinksWithAccess","CognosService", "userCapabilities is null, returning empty array")
        return []
      }
    
      this.logger.info("getLinksWithAccess","CognosService", "this.config.target: "+this.config.target)
      const scenarioProperties = { ...this.userCapabilities.getValue()[this.config.target] };
    
      this.logger.info("getLinksWithAccess","CognosService", "scenarioProperties: "+JSON.stringify(scenarioProperties))
      for (const prop in scenarioProperties) {
        // Delete report keys without access
        if (!scenarioProperties[prop]) delete scenarioProperties[prop]
      }

      this.logger.info("getLinksWithAccess","CognosService", "scenarioProperties: "+JSON.stringify(scenarioProperties))
      // Filter links by user capabilities
      const haveAccessTo = Object.keys(scenarioProperties);
      this.logger.info("getLinksWithAccess","CognosService", "haveAccessTo: "+JSON.stringify(haveAccessTo))
      
      const linksReturn = links.filter(link => haveAccessTo.includes(link.text))
      this.logger.info("getLinksWithAccess","CognosService", "linksReturn: "+JSON.stringify(linksReturn))
      
      return linksReturn;
    } else {
      // Return links without filter in Development
      this.logger.info("getLinksWithAccess","CognosService", "returning links without filter in Development")
      return links;
    }
  }

  /**
   * Function to initialize App requesting a secured item and providing loginForm depending on repsonse with
   * @param config ConfigService (automatic)
   */
  async load(config: Config, resolve: () => void): Promise<void> {

    this.logger.info("load","CognosService", "corpintra:"+this.config.corpintra+" localStorage.getItem('co_corpintra'): "+localStorage.getItem("co_corpintra"))
    this.logger.debug("load","CognosService", "config.corpintra: "+config.corpintra)
    
    // if config.apiDomain is not set, use the default value from localStorage
    if (config.apiDomain == "") {
      // Check apiDomain is set in localStorage
      if (localStorage.getItem("co_apiDomain") == null) {
        this.logger.debug("load","CognosService", "! config.apiDomain is not set, and no apiDomain is set in localStorage")
        // set apDomain based on browser location
        config.apiDomain = window.location.protocol + '//' + window.location.host + window.location.pathname
        // Remove file name from path
        config.apiDomain = config.apiDomain.split('/').slice(0, -1).join('/')
        this.logger.info("load","CognosService", "! config.apiDomain is not set, use the default value from browser location: "+config.apiDomain)
      } else {
        config.apiDomain = localStorage.getItem("co_apiDomain")
        this.logger.info("load","CognosService", "! config.apiDomain is not set, use the default value from localStorage: "+config.apiDomain)
      }
    }
    // Ensure apiDomain ends with a slash
    if (!config.apiDomain.endsWith('/')) {
      config.apiDomain += '/';
    }

    const url = `${config.apiDomain}${config.apiLink}login`

    this.logger.info("load","CognosService", "Url:"+url)
    this.logger.info("load","CognosService", "Trying to get login page - if successful, continue App load process, otherwise login will be done with redirect or iFrame")
      
    try {
      const response = await firstValueFrom(
        this.http.get(url, {
          responseType: 'text',
          observe: 'response'
        })
        .pipe(
          tap(response => {
            this.logger.info("load", "CognosService", "status: " + response.status);
            this.logger.info("load", "CognosService", "response: " + JSON.stringify(response));
          })
        )
      );

      // Load user capabilities and continue App load process
      this.logger.info("load","CognosService", "Success: Now load user capabilities and continue App load process");
      return this.loadCapabilities(resolve, config);
    } catch (err) {
      this.logger.info("load","CognosService", "User is not authenticated, use old method with iframe to log in: "+ JSON.stringify(err));
      this.logger.info("load","CognosService", "err.url: "+err.error);
      this.logger.info("load","CognosService", "calling doLogoutAndLogin to fix the issue")
      return this.doLogoutAndLogin(config);
    }
  }

  /*
   * Does logout and login using the logout endpoint
   * @param config ConfigService or ConfigState
   */
  async doLogoutAndLogin(config: Config) {
    // 1. Call the logout endpoint (relative URL works everywhere)
    try {
      this.logger.info("doLogoutAndLogin","CognosService", `calling logout endpoint: ${config.apiDomain}${config.apiLink}dispatch/logout`)
      await fetch(`${config.apiDomain}${config.apiLink}dispatch/logout`, { credentials: 'include' });
      this.logger.info("doLogoutAndLogin","CognosService", "waited for logout endpoint response")
    } catch (e) {
      this.logger.info('Logout request failed (might already be logged out)', e);
    }
  
    // 2. Detect current deployment path (e.g. /int12, /int, or /prod)
    const pathMatch = location.pathname.match(/^(\/(?:int12|prod12|int|prod))/);
    const basePaths = pathMatch ? pathMatch[1] : '/';
    const cookiePaths = basePaths === '/' ? ['/'] : [basePaths, `${basePaths}/bi`];

    // 3. All possible domains we need to clear cookies for
    const domains = [
      '',                                   // current domain (no domain= → path-only cookie)
      location.hostname,                    // e.g. cmdip.app.tbintra.net
      '.' + location.hostname,              // e.g. .cmdip.app.tbintra.net (subdomain wildcard)
    ];
  
    // 4. Cookies we need to kill
    const cookiesToKill = [
      'caf',
      'usersessionid',
      'userCapabilities',
      'userCapabilitiesEx',
      'XSRF-TOKEN',
      'CRN',
      'cea-ssa'
    ];
  
    // 5. Wipe them all (both /int12 and /int12/bi — Cognos sets cookies on the BI path)
    cookiesToKill.forEach(name => {
      cookiePaths.forEach(path => {
        domains.forEach(dom => {
          const cookieStr = [
            `${name}=`,
            dom ? `domain=${dom}` : '',
            `path=${path}`,
            'expires=Thu, 01 Jan 1970 00:00:00 GMT',
            'Secure',
            'SameSite=None'
          ].filter(Boolean).join('; ');
          this.logger.info("doLogoutAndLogin","CognosService", "setting cookie: "+cookieStr)
          document.cookie = cookieStr;
        });
      });
    });

    // 6.: mark that we want to go back to DIPmore after re-login
    this.logger.info("doLogoutAndLogin","CognosService", "setting localstorage item [amv_return_to_dipmore] to true")
    localStorage.setItem('amv_return_to_dipmore', 'true');
  
    // 7. Redirect to the Cognos portal root (always safe)
    this.logger.info("doLogoutAndLogin","CognosService", `redirecting to: ${config.apiDomain}${config.portal}`)
    window.location.href = `${config.apiDomain}${config.portal}`;
  }


  /**
   * Loads the user capabilities array and sets reports access
   * @param resolve callback of parent .load()
   * @param config ConfigService or ConfigState
   */
  loadCapabilities(resolve, config: Config) {
    this.logger.info("loadCapabilities","CognosService", "starting loadCapabilities")
    if (config.loadCapabilities === "in progress") {
      this.logger.info("loadCapabilities","CognosService", "loadCapabilities already in progress, skipping")
      return;
    }
    config.loadCapabilities = "in progress";

    // Do a request to know the preferences and basic info of the logged user
    // Retrieve the report containing a list of users and their permissions to view reports
    this.getUserCapabilities(config)
    .pipe(
      catchError(error => {
        this.logger.warn("loadCapabilities","CognosService", "Error at retrieving capabilities: "+JSON.stringify(error));
        return of({ success: false, data: []});
      })
    )
    .subscribe(data => {
      this.logger.info("loadCapabilities","CognosService", "received data, will evaluate on the result")
      if (data.success) {
        this.logger.info("loadCapabilities","CognosService", "data.success: "+JSON.stringify(data.success))

        // Filter array by our App DIPRE
        const re = new RegExp(/CAMID\(\"\:0201_DIPRE\:_(.*)\"\)/);
        let rows = data.data.reduce((r, a) => {
          const match = re.exec(a.searchPath);
          if (match) {
            r.push(match[1]);
          }
          return r;
        }, []);
        // Set user capabilities object values by name
        this.userCapabilities.next({
          admin: rows.some(permission => permission.toLowerCase() === 'Global_Function_Groups‬:DIPRE_Admins'.toLowerCase()),
          mobile: rows.some(permission => permission.toLowerCase() === 'Global_Function_Groups‬:DIPRE_Mobile'.toLowerCase()),
          trucks: {
            order_intake: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Order Intake'.toLowerCase()),
            order_intake_history: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Order_History'.toLowerCase()),
            order_backlog: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Order_Back_Log'.toLowerCase()),
            production_program: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Production Program'.toLowerCase()),
            allocation: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Allocation'.toLowerCase()),
            plant_stock: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Plant Stock'.toLowerCase()),
            deliveries: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Truck_Management_Deliveries'.toLowerCase()),
          },
          vans: {
            order_intake: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_VAN_Management_Order Intake'.toLowerCase()),
            order_intake_history: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Van_Management_Order_History'.toLowerCase()),
            order_backlog: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_Van_Management_Order_Back_Log'.toLowerCase()),
            production_program: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_VAN_Management_Production Program'.toLowerCase()),
            allocation: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_VAN_Management_Allocation'.toLowerCase()),
            plant_stock: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_VAN_Management_Plant Stock'.toLowerCase()),
            deliveries: rows.some(permission => permission.toLowerCase() === 'Project_Function_Groups:Management Function:DIPRE_VAN_Management_Deliveries'.toLowerCase()),
          }
        });

        this.logger.info("loadCapabilities","CognosService", "userCapabilities: "+JSON.stringify(this.userCapabilities.getValue()))

        config.loadCapabilities = "done";
        // return resolve();
      } else {
        this.logger.warn("loadCapabilities","CognosService", "Fail at retrieving capabilities!! This will prevent the app from loading.");
      }
    });
  }

  // Get user capabilites retrieving them from Security_Report
  getUserCapabilities(config: Config): Observable<{ success: boolean, data?: any[], error?: string, more?: any }> {
    // make sure this is only called once
    if (this.userCapabilities.getValue() !== null) {
      this.logger.info("getUserCapabilities","CognosService", "userCapabilities already set, skipping")
    }
    this.logger.info("getUserCapabilities","CognosService", "getting capabilities from server: "+config.apiDomain+config.apiLink)
    if (config.corpintra) {
      // Create the temporary URL and check trailing slash on apiLink
      let url = `${config.apiDomain}${config.apiLink}`
      if (!url.endsWith('/')) {
        url += '/';
      }
      url +="identity"

      // Return XHR of user capabilities
      this.logger.info("getUserCapabilities","CognosService", "url:"+url)
      
      return this.http.get<any>(url).pipe(
        tap(rows => {
          this.logger.info("getUserCapabilities","CognosService", JSON.stringify(rows))
        }),
        map(rows => ({ success: true, data: rows.data })),
        catchError(err => {
          this.logger.warn("getUserCapabilities","CognosService", "Fail at retrieving capabilities. Error: "+JSON.stringify(err))
          return of({ success: false, data: [], error: 'CAP - Fail at retrieving report info.', more: err });
        })
      );
    } else {
      this.logger.info("getUserCapabilities","CognosService", "corpintra is false, nothing to do here")
      return of({ success: false, data: [] });
    }
  }
}
